import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork } from 'app/core/core.module';
import { PurchaseOrderInvoice, PurchaseOrderInvoiceLine, PurchaseOrderLineReceiving } from 'app/core/model/entity-model';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { PurchaseOrderInvoiceDialogComponent } from '../purchase-order-invoice-dialog/purchase-order-invoice-dialog.component';
import moment from 'moment';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';

@Component({
    selector: 'app-purchase-order-receivings',
    templateUrl: './purchase-order-receivings.component.html',
    styleUrls: ['./purchase-order-receivings.component.scss'],
    standalone: false,
})
export class PurchaseOrderReceivingsComponent implements OnInit {
    receivings: any;
    showCompleted = false;
    canCreateBills = false;
    selectedTotal = 0;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    constructor(private uow: GenesisUnitOfWork, private router: Router, public auth: AuthService, private modalService: BsModalService, private appInfo: AppInformationService) {}

    async ngOnInit() {
        this.setPermissions();
        await this.getData();
    }

    setPermissions() {
        this.canCreateBills = true;
    }

    async getData() {
        let receivings = null;
        const vendors = await this.uow.vendors.all();
        receivings = (await this.uow.getNonBilledReceivings([
            'purchaseOrderLine.purchaseOrder',
            'purchaseOrderLine.part',
            'purchaseOrderInvoiceLines.purchaseOrderInvoice',
        ])) as PurchaseOrderLineReceiving[];
        this.receivings = receivings;
        // console.log(this.receivings);
    }

    // this method is used to clear the selection when the user searches for something
    onOptionChanged(e) {
        if (e.fullName === 'searchPanel.text') {
            e.component.__searchChanged = true;
        }
        const filterCheck = _.includes(e.fullName, 'filterValue');
        if (filterCheck) {
            this.dataGrid.instance.clearSelection();
        }
    }

    // this method is used to clear the selection when the user searches for something
    onContentReady(e) {
        if (e.component.__searchChanged) {
            //__searchChanged is for the search box
            e.component.__searchChanged = false;
            this.dataGrid.instance.clearSelection();
        }
    }

    selectionChange(e) {
        this.selectedTotal = 0;
        if (!e) return;

        if (!!e.selectedRowsData) {
            e.selectedRowsData.forEach((row) => {
                this.selectedTotal += row.quantity * row.purchaseOrderLine.cost;
            });
        }
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarTextBefore',
                location: 'before',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            }
        );
    }

    async rowPrepare(e) {
        if (e.rowType === 'data' && !!e.data.deleted) {
            e.rowElement.className = e.rowElement.className + ' gray-grid';
        }
    }

    async open(id: number) {
        this.router.navigate(['/purchase-orders/' + (!!id ? id : 'new')]);
    }
    async rowDoubleClick(e) {
        console.log(e);
        if (!e.data || !e.data.id) return;
        await this.open(e.data.purchaseOrderLine.purchaseOrderId);
    }

    async readyToExport() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) return;

        const result = await (confirm(
            `Are you sure you want to mark the ${selectedRows.length === 1 ? 'receiving' : 'receivings'} as Ready To Export for Quickbooks?`,
            `${this.appInfo.settings.company}`
        ) as Promise<boolean>);
        if (!result) return;

        selectedRows.forEach((row) => {
            if (!row.readyToExport) row.readyToExport = new Date();
        });
        await this.uow.commit();
        await this.getData();
    }

    async delete() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (selectedRows.length !== 1) return;
        const selectedRow = selectedRows[0];

        const result = await confirm(`Are you sure you want to DELETE this record?`, `${this.appInfo.settings.company}`);
        if (!result) return;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Enter Reason For Deletion',
                dialogPrompt: 'Enter Reason For Deletion',
                mode: 'text',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            modalRef.hide();
            if (result.result && !!result.value && result.value.length > 0) {
                this.appInfo.pleaseWaitShow();
                selectedRow.deleted = new Date();
                selectedRow.deletedBy = this.auth.currentUser.id;
                const PONote = `Receiving for Part ${selectedRow.purchaseOrderLine.partNumber} Deleted ${moment(result.deleted).format('MM/DD/YYYY')} by ${
                    this.auth.currentUser.userName
                }: ${result.value}`;
                selectedRow.purchaseOrderLine.purchaseOrder.note = selectedRow.purchaseOrderLine.purchaseOrder.note ? selectedRow.purchaseOrderLine.purchaseOrder.note + '\n' + PONote : PONote;
                await this.uow.commit();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            } else {
                await alert('Reason required.', this.appInfo.settings.company);
            }
        });
    }

    async createBill(zeroMode: boolean) {
        const receivings = this.dataGrid.instance.getSelectedRowsData() as PurchaseOrderLineReceiving[];
        if (!receivings) return;

        const vendor = receivings[0].purchaseOrderLine.purchaseOrder.vendor;
        const hasSameVendor = receivings.every((receiving) => receiving.purchaseOrderLine.purchaseOrder.vendor.id === vendor.id);
        if (!hasSameVendor) {
            await alert('Selected Rows must all have the same Vendor', this.appInfo.settings.company);
            return;
        }

        const alreadyOnPOInvoice = receivings.some((receiving) => receiving.purchaseOrderInvoiceLines.filter((x) => !x.deleted).length > 0);
        if (alreadyOnPOInvoice) {
            await alert('One or more of the selected rows already has a purchase order invoice', this.appInfo.settings.company);
            return;
        }

        const lineIds = receivings.map((line) => line.id);
        const modalRef = this.modalService.show(PurchaseOrderInvoiceDialogComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                vendor: vendor,
                lineIds: lineIds,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async payWithoutQuickBooks() {
        const receivings = this.dataGrid.instance.getSelectedRowsData() as PurchaseOrderLineReceiving[];
        if (!receivings) return;

        const vendor = receivings[0].purchaseOrderLine.purchaseOrder.vendor;
        const hasSameVendor = receivings.every((receiving) => receiving.purchaseOrderLine.purchaseOrder.vendor.id === vendor.id);
        if (!hasSameVendor) {
            await alert('Selected Rows must all have the same Vendor', this.appInfo.settings.company);
            return;
        }

        const alreadyOnPOInvoice = receivings.some((receiving) => receiving.purchaseOrderInvoiceLines.filter((x) => !x.deleted).length > 0);
        if (alreadyOnPOInvoice) {
            await alert('One or more of the selected rows already has a purchase order invoice', this.appInfo.settings.company);
            return;
        }
        const result = await confirm('Do you want to automatically mark as Paid w/o Quickbooks?', this.appInfo.settings.company);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        const today = new Date();
        const invoice = this.uow.purchaseOrderInvoices.createEntity({
            vendorId: vendor.id,
            invoiceDate: moment(today).startOf('day').toDate(),
            dueDate: moment(today).add(30, 'days').startOf('day').toDate(),
            created: today,
            createdBy: this.auth.currentUser.userName,
        }) as PurchaseOrderInvoice;
        receivings.forEach((receiving) => {
            const line = this.uow.purchaseOrderInvoiceLines.createEntity({
                purchaseOrderInvoiceId: invoice.id,
                purchaseOrderLineReceivingId: receiving.id,
                purchaseOrderLineItemId: receiving.purchaseOrderLine.purchaseOrderLineItemId,
                quantity: receiving.quantity,
                cost: receiving.purchaseOrderLine.cost,
                created: today,
                createdBy: this.auth.currentUser.userName,
            }) as PurchaseOrderInvoiceLine;
        });
        invoice.total = 0;
        invoice.quickBooksLastExport = new Date();
        invoice.quickBooksId = 'Marked paid w/o QB';
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
        await alert('The bill has been marked as Paid w/o Quickbooks.', this.appInfo.settings.company);
    }
}
